/* ─────────────────────────────────────────────────────────────
   ListBuilder Renderer — Config-driven add/remove list pattern.
   ENT-41: Unified read-only rendering (F41.A) + In-place edit (F41.B)
           + Agent confidence indicator (F41.C)
   ───────────────────────────────────────────────────────────── */

import { useState, useCallback, useMemo, useId } from 'react';
import { Pencil, Plus, Trash2, Check, X } from 'lucide-react';
import type { FieldRendererProps, ListBuilderConfig, ListBuilderSubField, RuntimeContext } from '../../config/types';
import { CURRENCIES } from './data';
import { Combobox } from '@/components/ui/Combobox';
import { Input } from '@/components/ui/Input';
import { DatePicker } from '@/components/ui/DatePicker';
import { Text } from '@/components/ui/Text';
import { Card } from '@/components/ui/Card';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/Tooltip';
import { PartyPicker } from '@/components/shared/PartyPicker';
import { CreatePersonDialog } from '@/components/shared/CreatePersonDialog';
import { MoneyControl } from './MoneyField';
import { moneyFormatError } from './moneyFormat';
import { ComputedControl } from './ComputedField';
import { CatalogueSelectControl } from './CatalogueSearchSelectField';

type ItemRecord = Record<string, string>;

/**
 * Merge prefill values into a row: fills EMPTY sub-fields only, never a value the user already
 * typed, and never the picker's own slot. Returns `prev` unchanged when the picker has since moved
 * to another party (a late response for an earlier pick must not land).
 */
function mergeRowPrefill(
  prev: ItemRecord,
  pickerField: string,
  partyId: string,
  prefill: Record<string, string>,
  subFields: ListBuilderSubField[],
): ItemRecord {
  if (prev[pickerField] !== partyId) return prev;
  const next = { ...prev };
  let changed = false;
  for (const sf of subFields) {
    if (sf.field === pickerField) continue;
    const incoming = prefill[sf.field];
    if (incoming && !(prev[sf.field] ?? '').trim()) {
      next[sf.field] = incoming;
      changed = true;
    }
  }
  return changed ? next : prev;
}

/**
 * Returns a callback to run after a row value changes: when the changed sub-field is a
 * partyPicker and the page supplied `runtimeContext.resolveListRowPrefill`, resolve that
 * party and merge the result into the row via `setRow` (fill-empty only).
 */
function useRowPrefill(
  listFieldId: string,
  subFields: ListBuilderSubField[] | undefined,
  runtimeContext: RuntimeContext,
  setRow: (update: (prev: ItemRecord) => ItemRecord) => void,
) {
  const resolve = runtimeContext.resolveListRowPrefill;
  return useCallback((fieldName: string, val: string) => {
    if (!resolve || !val || !subFields) return;
    const sf = subFields.find((s) => s.field === fieldName);
    if (sf?.inputType !== 'partyPicker') return;
    resolve(listFieldId, val)
      .then((prefill) => setRow((prev) => mergeRowPrefill(prev, fieldName, val, prefill, subFields)))
      .catch(() => { /* prefill is a convenience — the row stays as typed */ });
  }, [resolve, listFieldId, subFields, setRow]);
}

// ─── SubFieldInput ───────────────────────────────────────────

const OTHER_SENTINEL = '__OTHER__';

function SubFieldInput({
  subField,
  value,
  onChange,
  runtimeContext,
  disabled = false,
  parentValue,
  controlId,
}: {
  subField: ListBuilderSubField;
  value: string;
  onChange: (v: string) => void;
  runtimeContext: RuntimeContext;
  /** When true, all input branches render in disabled/read-only mode. */
  disabled?: boolean;
  /** For dependentSelect: current value of the dependsOn sibling field. */
  parentValue?: string;
  /**
   * Id of this control; its {@link SubFieldLabel} is `${controlId}-label`. Every branch wires
   * both, so each list field has an accessible name (labels were not associated before EW-T15).
   */
  controlId?: string;
}) {
  const labelId = controlId ? `${controlId}-label` : undefined;
  // dependentSelect: options keyed by parentValue in runtimeContext[dataSource.key].
  // isOtherPending resets naturally when parentValue changes because the caller passes
  // key={parentValue} on SubFieldInput, which causes a remount with fresh state.
  const [isOtherPending, setIsOtherPending] = useState(false);
  // partyPicker: null = "Add new person" closed; a string = open, seeded with the search text.
  const [addPersonName, setAddPersonName] = useState<string | null>(null);

  // ENT-565 FR-67: the same controls the top-level `money`, `computed` and `catalogueSearchSelect`
  // fields draw, so a list builder's sub-fields behave exactly like those fields.
  if (subField.inputType === 'money') {
    return (
      <MoneyControl
        id={controlId}
        ariaLabelledBy={labelId}
        value={value}
        onChange={onChange}
        disabled={disabled}
        currency={typeof subField.props?.currency === 'string' ? subField.props.currency : undefined}
        placeholder={subField.placeholder}
      />
    );
  }
  if (subField.inputType === 'computed') {
    // Read-only by construction: `onChange` is never called.
    return <ComputedControl id={controlId} value={value} testId={typeof subField.props?.testId === 'string' ? subField.props.testId : undefined} />;
  }
  if (subField.inputType === 'catalogueSearchSelect') {
    return (
      <CatalogueSelectControl
        id={controlId}
        ariaLabelledBy={labelId}
        value={value}
        kind={typeof subField.props?.catalogueKind === 'string' ? subField.props.catalogueKind : undefined}
        needsParent={subField.dependsOn !== undefined}
        parentKey={parentValue || undefined}
        disabled={disabled}
        onChange={(key) => onChange(key)}
      />
    );
  }

  if (subField.inputType === 'dependentSelect') {
    const contextKey = subField.dataSource?.type === 'context' ? subField.dataSource.key : null;
    const taxMap = contextKey
      ? (runtimeContext[contextKey] as Record<string, string[]> | undefined) ?? {}
      : {};
    const rawOptions = parentValue ? (taxMap[parentValue] ?? []) : [];
    const selectOptions = rawOptions.map((r) => ({ value: r, label: r }));
    // Always append "Other…" when allowOther — even if rawOptions is empty so the
    // user can enter a free-text role when the taxonomy list is absent or still loading.
    if (subField.allowOther) {
      selectOptions.push({ value: OTHER_SENTINEL, label: 'Other…' });
    }

    // Show text input when user selected "Other" or value is already a custom string
    const isOtherActive = isOtherPending || (value !== '' && !rawOptions.includes(value));
    // Combobox, matching the plain-select branch below and the top-level SelectField (W4.T5.7).
    // Empty is '' rather than an 'All' sentinel — Combobox models "nothing chosen" directly.
    // OTHER_SENTINEL still round-trips: selecting it arms the free-text Input underneath.
    const selectValue = isOtherActive ? OTHER_SENTINEL : (value || '');

    return (
      <div>
        <Combobox
          id={controlId}
          ariaLabelledBy={labelId}
          value={selectValue}
          onValueChange={(v) => {
            if (v === OTHER_SENTINEL) {
              setIsOtherPending(true);
              onChange('');
            } else {
              setIsOtherPending(false);
              onChange(v);
            }
          }}
          options={selectOptions}
          placeholder={parentValue ? (subField.placeholder ?? 'Select…') : 'Select type first…'}
          className="bg-surface-elevated"
          clearable
          disabled={disabled || !parentValue}
        />
        {isOtherActive && subField.allowOther && (
          <Input
            value={value}
            onChange={(e) => { setIsOtherPending(false); onChange(e.target.value); }}
            placeholder="Enter role title"
            className="mt-1.5"
            aria-label={`Custom ${subField.label}`}
            disabled={disabled}
          />
        )}
      </div>
    );
  }

  if (subField.inputType === 'select' || subField.inputType === 'currencySelect') {
    // For currencySelect, float the suggested currency to the top of the list.
    const suggestedCurrency = runtimeContext.suggestedCurrency as string | undefined;
    const builtInOptions = subField.inputType === 'currencySelect'
      ? (suggestedCurrency
          ? [
              ...CURRENCIES.filter((c) => c.value === suggestedCurrency),
              ...CURRENCIES.filter((c) => c.value !== suggestedCurrency),
            ]
          : CURRENCIES)
      : [];
    const contextOptions = subField.dataSource?.type === 'context'
      ? (runtimeContext[subField.dataSource.key] as { value: string; label: string }[] | undefined) ?? []
      : null;
    const options = subField.dataSource?.type === 'static'
      ? subField.dataSource.options
      : contextOptions !== null
        ? contextOptions
        : builtInOptions;

    // Context select with no options — empty-state UX is only shown in editable mode
    // (add-form). In disabled / read-only mode we MUST fall through to the normal
    // Combobox path below so that the row's actual `value` (e.g. an agent-
    // resolved partyId) is preserved rather than swapped with the "All" sentinel
    // and an empty options array. If the runtime options reload later, the label
    // will resolve naturally.
    if (
      !disabled &&
      subField.dataSource?.type === 'context' &&
      options.length === 0
    ) {
      return (
        <div>
          <Combobox
            id={controlId}
            ariaLabelledBy={labelId}
            options={[]}
            placeholder={subField.placeholder ?? 'Select...'}
            className="bg-surface-elevated"
            disabled
          />
          {subField.emptyStateAction && (
            <a
              href={subField.emptyStateAction.href}
              className="mt-1 text-body-sm text-primary hover:underline flex items-center gap-1"
            >
              {subField.emptyStateAction.label} →
            </a>
          )}
        </div>
      );
    }

    /*
     * Combobox, not FilterSelect (W4.T5.7).
     *
     * A config `inputType: 'select'` rendered a FilterSelect here and a Combobox at the top level
     * (SelectField.tsx:127), so the SAME declared field type produced two different controls
     * depending on whether it sat in a list row — visibly different dropdowns inside one form.
     *
     * The 'All' sentinel goes with it: it was FilterSelect's way of representing "nothing
     * chosen". Combobox is `clearable` and models empty as empty, which is what the caller's
     * onChange already expected.
     */
    return (
      <Combobox
        id={controlId}
        ariaLabelledBy={labelId}
        options={options}
        value={value || ''}
        onValueChange={(v) => onChange(v)}
        placeholder={subField.placeholder ?? 'Select...'}
        disabled={disabled}
        clearable
        className="bg-surface-elevated"
      />
    );
  }

  if (subField.inputType === 'partyPicker') {
    // Same shared picker + create dialog as the top-level partyPicker renderer — no fork.
    const actingTenantId = runtimeContext.actingTenantId as string | undefined;
    const allowCreate = subField.allowCreatePerson === true && !disabled;
    return (
      <>
        <PartyPicker
          id={controlId}
          ariaLabelledBy={labelId}
          value={value || undefined}
          onChange={onChange}
          placeholder={subField.placeholder ?? 'Search people...'}
          personsOnly={subField.personsOnly ?? true}
          actingTenantId={actingTenantId}
          disabled={disabled}
          className="bg-surface-elevated"
          onCreatePerson={allowCreate ? (searchText) => setAddPersonName(searchText ?? '') : undefined}
        />
        {allowCreate && addPersonName !== null && (
          <CreatePersonDialog
            open
            initialName={addPersonName}
            actingTenantId={actingTenantId}
            onClose={() => setAddPersonName(null)}
            onCreated={(person) => onChange(person.party_id)}
          />
        )}
      </>
    );
  }

  if (subField.inputType === 'date') {
    return (
      <DatePicker
        id={controlId}
        value={value}
        onValueChange={onChange}
        placeholder={subField.placeholder ?? 'Pick a date'}
        disabled={disabled}
      />
    );
  }

  const type = subField.inputType === 'numberField' ? 'number' : 'text';

  return (
    <Input
      id={controlId}
      aria-required={subField.required || undefined}
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={subField.placeholder}
      disabled={disabled}
      className={type === 'number' ? 'font-mono tabular-nums' : undefined}
    />
  );
}

// ─── SubFieldLabel ───────────────────────────────────────────
// The visible label of one sub-field, associated with its control: `htmlFor` for native
// controls, and `id` so combobox-style controls (which otherwise name themselves after their
// placeholder via aria-label) can point back with aria-labelledby. The required asterisk sits
// OUTSIDE the <label> (and is aria-hidden), so both the accessible name and the label's text are
// the label alone — getByLabelText('PAN') and getByRole(..., { name: 'PAN' }) both match.

function SubFieldLabel({
  subField,
  controlId,
  className,
}: {
  subField: ListBuilderSubField;
  controlId: string;
  className?: string;
}) {
  return (
    <div className={className ?? 'flex items-center gap-1'}>
      <Text variant="label-sm" color="subtle" as="label" id={`${controlId}-label`} htmlFor={controlId} className="truncate">
        {subField.label}
      </Text>
      {subField.required && <span aria-hidden="true" className="text-danger">*</span>}
    </div>
  );
}

// ─── CompactSubField ─────────────────────────────────────────
// Read-only row: label on the left, disabled SubFieldInput on the right.
// Uses SubFieldInput so label resolution (UUID→name) is handled identically
// to the add-form's interactive variant.

/** Resolve a select-typed subField's value to a human-readable label.
 * Returns the matched option label for static or context selects, the
 * COUNTRIES/CURRENCIES label for built-in selects, or `null` if no match
 * (caller decides whether to show the raw value as a fallback). */
function resolveSubFieldLabel(
  subField: ListBuilderSubField,
  value: string,
  runtimeContext: RuntimeContext,
): string | null {
  if (subField.inputType === 'currencySelect') {
    return CURRENCIES.find((o) => o.value === value)?.label ?? null;
  }
  // dependentSelect stores plain human-readable values (e.g. role titles) — show as-is.
  if (subField.inputType === 'dependentSelect') return null;
  if (subField.inputType !== 'select') return null;

  const ds = subField.dataSource;
  if (ds?.type === 'static') {
    return ds.options.find((o) => o.value === value)?.label ?? null;
  }
  if (ds?.type === 'context') {
    const opts = (runtimeContext[ds.key] as { value: string; label: string }[] | undefined) ?? [];
    return opts.find((o) => o.value === value)?.label ?? null;
  }
  return null;
}

function CompactSubField({
  subField,
  value,
  runtimeContext,
  controlId,
}: {
  subField: ListBuilderSubField;
  value: string;
  runtimeContext: RuntimeContext;
  controlId: string;
}) {
  // Skip empty / unset fields in read mode
  if (!value && value !== '0') return null;

  // For select-typed fields where the option lookup misses (options not loaded
  // yet, or value references a record outside the current dropdown set), show
  // the raw value as a fallback so the user can see *something* — not a blank
  // trigger. Long UUID-like values are truncated to head…tail for readability.
  const isSelectLike =
    subField.inputType === 'select' ||
    subField.inputType === 'currencySelect' ||
    subField.inputType === 'dependentSelect';
  const resolvedLabel = isSelectLike
    ? resolveSubFieldLabel(subField, value, runtimeContext)
    : null;
  const showRawFallback = isSelectLike && resolvedLabel === null;
  const truncatedRaw = value.length > 24
    ? `${value.slice(0, 8)}…${value.slice(-4)}`
    : value;

  return (
    <div className="flex items-center gap-3 py-1 border-b border-border/40 last:border-0">
      {showRawFallback ? (
        <Text variant="label-sm" color="subtle" className="w-28 shrink-0 truncate">
          {subField.label}
        </Text>
      ) : (
        <SubFieldLabel subField={{ ...subField, required: false }} controlId={controlId} className="w-28 shrink-0 truncate" />
      )}
      {/* pointer-events-none prevents any accidental focus/click on the disabled input */}
      <div className="flex-1 min-w-0 pointer-events-none">
        {showRawFallback ? (
          <Text variant="body-sm" color="muted" className="font-mono font-mono-face truncate block" title={value}>
            {truncatedRaw}
          </Text>
        ) : (
          <SubFieldInput
            subField={subField}
            value={value}
            onChange={() => {/* read-only */}}
            runtimeContext={runtimeContext}
            disabled
            controlId={controlId}
          />
        )}
      </div>
    </div>
  );
}

// ─── EditSubField ────────────────────────────────────────────
// Editable row within an EditCard: label + active SubFieldInput + optional error.

function EditSubField({
  subField,
  value,
  onChange,
  runtimeContext,
  error,
  draftValues,
  controlId,
}: {
  subField: ListBuilderSubField;
  value: string;
  onChange: (v: string) => void;
  runtimeContext: RuntimeContext;
  controlId: string;
  error?: string;
  /** Full current draft — used to compute the parentValue for dependentSelect. */
  draftValues?: ItemRecord;
}) {
  const parentValue = subField.dependsOn ? (draftValues?.[subField.dependsOn] ?? '') : undefined;
  return (
    <div className="space-y-1 mb-3 last:mb-0">
      <SubFieldLabel subField={subField} controlId={controlId} />
      <SubFieldInput
        key={parentValue}
        subField={subField}
        value={value}
        onChange={onChange}
        runtimeContext={runtimeContext}
        disabled={false}
        parentValue={parentValue}
        controlId={controlId}
      />
      {subField.helpText && !error && (
        <Text variant="body-sm" color="muted">{subField.helpText}</Text>
      )}
      {error && (
        <Text variant="body-sm" color="danger">{error}</Text>
      )}
    </div>
  );
}

// ─── Validation ──────────────────────────────────────────────

function validateEditDraft(
  editDraft: ItemRecord,
  config: ListBuilderConfig,
): { fieldErrors: Record<string, string>; crossFieldError: string | null } {
  const fieldErrors: Record<string, string> = {};

  // Required-for-add check (same rules as add button canAdd)
  for (const fieldName of config.requiredForAdd ?? []) {
    if (!editDraft[fieldName]?.trim()) {
      const sf = config.subFields.find((s) => s.field === fieldName);
      fieldErrors[fieldName] = `${sf?.label ?? fieldName} is required`;
    }
  }

  // A money sub-field must be a plain decimal (digits, at most two decimals).
  for (const sf of config.subFields) {
    if (sf.inputType !== 'money' || fieldErrors[sf.field]) continue;
    const bad = moneyFormatError(editDraft[sf.field]);
    if (bad) fieldErrors[sf.field] = bad;
  }

  // Declarative cross-field rules from config
  for (const rule of config.crossFieldRules ?? []) {
    if (rule.op === 'lte') {
      const lhsVal = Number(editDraft[rule.lhs]);
      const rhsVal = Number(editDraft[rule.rhs]);
      if (editDraft[rule.lhs] && editDraft[rule.rhs] && !isNaN(lhsVal) && !isNaN(rhsVal) && lhsVal > rhsVal) {
        return { fieldErrors, crossFieldError: rule.message };
      }
    }
  }

  return { fieldErrors, crossFieldError: null };
}

// ─── ConfidenceIndicator ─────────────────────────────────────
// Small dot indicator for agent-supplied items with low/medium confidence.
//   >= 0.85  → hidden (high confidence, no review needed)
//   [0.5, 0.85) → amber dot — "Agent confidence: {n}%"
//   < 0.5    → red dot   — "Agent uncertain — please review"
// Clears automatically when the user edits and saves the row (T02 strip-on-save).

function ConfidenceIndicator({ confidence }: { confidence: number }) {
  if (confidence >= 0.85) return null;

  const isLow = confidence < 0.5;
  const pct = Math.round(confidence * 100);
  const ariaLabel = isLow
    ? 'Agent uncertain — please review'
    : `Agent confidence: ${pct}%`;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label={ariaLabel}
          className={`w-2 h-2 rounded-full shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 ${
            isLow
              ? 'bg-danger focus-visible:ring-danger'
              : 'bg-warning focus-visible:ring-warning'
          }`}
        />
      </TooltipTrigger>
      <TooltipContent>{ariaLabel}</TooltipContent>
    </Tooltip>
  );
}

// ─── ReadCard ────────────────────────────────────────────────
// Displays an item in compact read-only mode with edit/remove affordances.

function ReadCard({
  item,
  config,
  runtimeContext,
  idPrefix,
  heading,
  onRemove,
  onEditStart,
}: {
  item: ItemRecord;
  config: ListBuilderConfig;
  runtimeContext: RuntimeContext;
  /** Unique per list instance and row; each sub-field's control id is `${idPrefix}-${field}`. */
  idPrefix: string;
  /** Optional card heading, e.g. "Directors 1" (from config.itemLabel). */
  heading?: string;
  onRemove: () => void;
  onEditStart: () => void;
}) {
  // Read _confidence from the item — stored as a number by the agent patch path.
  // ItemRecord is Record<string, string> so we narrow via cast.
  const rawConfidence = (item as Record<string, unknown>)['_confidence'];
  const confidence = typeof rawConfidence === 'number' ? rawConfidence : undefined;

  return (
    <Card variant="plain" className="p-3.5">
      {heading && (
        <Text variant="label-lg" as="h4" className="mb-2">{heading}</Text>
      )}
      {confidence !== undefined && (
        <div className="mb-2">
          <ConfidenceIndicator confidence={confidence} />
        </div>
      )}

      {/* Subfield rows — rendered in subFields order, skipping empty values */}
      <div>
        {config.subFields.map((sf) => (
          <CompactSubField
            key={sf.field}
            subField={sf}
            value={item[sf.field] ?? ''}
            runtimeContext={runtimeContext}
            controlId={`${idPrefix}-${sf.field}`}
          />
        ))}
      </div>

      {/* Footer: edit/remove buttons at the bottom for better visual hierarchy */}
      <div className="flex items-center justify-end gap-1 mt-2">
        <button
          type="button"
          onClick={onEditStart}
          aria-label="Edit item"
          className="p-1 text-text-subtle hover:text-text hover:bg-surface-muted rounded-lg transition-colors"
        >
          <Pencil size={14} />
        </button>
        <button
          type="button"
          onClick={onRemove}
          aria-label="Remove item"
          className="p-1 text-danger hover:bg-danger-subtle rounded-lg transition-colors"
        >
          <Trash2 size={14} />
        </button>
      </div>
    </Card>
  );
}

// ─── EditCard ────────────────────────────────────────────────
// Renders an item in full edit mode. Mounted fresh when edit starts (via key prop),
// so useState initial values are always derived from the current item — no effect needed.

function EditCard({
  item,
  config,
  runtimeContext,
  listFieldId,
  idPrefix,
  heading,
  onSave,
  onCancel,
}: {
  item: ItemRecord;
  config: ListBuilderConfig;
  runtimeContext: RuntimeContext;
  listFieldId: string;
  /** Unique per list instance and row; each sub-field's control id is `${idPrefix}-${field}`. */
  idPrefix: string;
  heading?: string;
  onSave: (updated: ItemRecord) => void;
  onCancel: () => void;
}) {
  // Initialize draft from item at mount. No effect needed — EditCard is mounted fresh
  // each time edit mode is entered (via key change in ListBuilderRenderer).
  const [editDraft, setEditDraft] = useState<ItemRecord>({ ...item });
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [crossFieldError, setCrossFieldError] = useState<string | null>(null);
  const prefillRow = useRowPrefill(listFieldId, config.subFields, runtimeContext, setEditDraft);

  const updateEditDraft = useCallback((fieldName: string, val: string) => {
    setEditDraft((prev) => {
      const next = { ...prev, [fieldName]: val };
      // Clear any dependentSelect whose parent field just changed
      for (const sf of config.subFields) {
        if (sf.inputType === 'dependentSelect' && sf.dependsOn === fieldName) {
          next[sf.field] = '';
        }
      }
      return next;
    });
    setFieldErrors((prev) => {
      if (!prev[fieldName]) return prev;
      const next = { ...prev };
      delete next[fieldName];
      return next;
    });
    setCrossFieldError(null);
    prefillRow(fieldName, val);
  }, [config.subFields, prefillRow]);

  const handleSaveClick = useCallback(() => {
    const { fieldErrors: errs, crossFieldError: crossErr } = validateEditDraft(editDraft, config);
    setFieldErrors(errs);
    setCrossFieldError(crossErr);
    if (Object.keys(errs).length > 0 || crossErr) return;

    // Strip any _-prefixed metadata fields (e.g., _confidence) from the saved item.
    // These are internal-only and must not be written back into the items array.
    const savedItem: ItemRecord = Object.fromEntries(
      Object.entries(editDraft).filter(([k]) => !k.startsWith('_'))
    );
    onSave(savedItem);
  }, [editDraft, config, onSave]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSaveClick();
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        onCancel();
      }
    },
    [handleSaveClick, onCancel],
  );

  return (
    <Card
      variant="plain"
      className="p-3.5 ring-1 ring-primary/30"
      onKeyDown={handleKeyDown}
      role="group"
      aria-label="Edit item"
    >
      {heading && (
        <Text variant="label-lg" as="h4" className="mb-2">{heading}</Text>
      )}
      {/* Edit form — all subfields enabled */}
      {config.subFields.map((sf) => (
        <EditSubField
          key={sf.field}
          subField={sf}
          value={editDraft[sf.field] ?? ''}
          onChange={(v) => updateEditDraft(sf.field, v)}
          runtimeContext={runtimeContext}
          error={fieldErrors[sf.field]}
          draftValues={editDraft}
          controlId={`${idPrefix}-${sf.field}`}
        />
      ))}

      {crossFieldError && (
        <Text variant="body-sm" color="danger" className="mt-1 mb-2">{crossFieldError}</Text>
      )}

      {/* Save / Cancel actions */}
      <div className="flex items-center gap-2 mt-3 pt-2 border-t border-border/40">
        <button
          type="button"
          onClick={handleSaveClick}
          aria-label="Save item changes"
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold bg-primary text-primary-foreground rounded-lg hover:bg-primary-strong transition-colors"
        >
          <Check size={13} />
          Save
        </button>
        <button
          type="button"
          onClick={onCancel}
          aria-label="Cancel item editing"
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-text-muted border border-border rounded-lg hover:bg-surface-muted transition-colors"
        >
          <X size={13} />
          Cancel
        </button>
      </div>
    </Card>
  );
}

// ─── ListBuilderRenderer ─────────────────────────────────────

export function ListBuilderRenderer({ field, value, onChange, runtimeContext }: FieldRendererProps) {
  const config = field.listBuilder;

  const items = useMemo(() => (value as ItemRecord[] | undefined) ?? [], [value]);
  const columns = config?.columns ?? 2;

  // Track which item index is currently in edit mode (only one at a time).
  // When non-null, the item at that index renders as EditCard (mounted fresh via key).
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  // Unique per list instance: two lists on one page (or one list rendered twice) never share
  // a label/control id.
  const uid = useId();

  // Add-form toggle: hidden by default; opened by the dashed "+ Add" button.
  const [showAddForm, setShowAddForm] = useState(false);

  const emptyDraft = useMemo(() => {
    const draft: ItemRecord = {};
    if (config) {
      for (const sf of config.subFields) {
        if (sf.defaultValue !== undefined) {
          draft[sf.field] = sf.defaultValue;
        } else if (sf.defaultFrom && runtimeContext[sf.defaultFrom] !== undefined) {
          draft[sf.field] = runtimeContext[sf.defaultFrom] as string;
        } else if (sf.field === 'currency_code' && runtimeContext.defaultCurrency) {
          draft[sf.field] = runtimeContext.defaultCurrency as string;
        } else {
          draft[sf.field] = '';
        }
      }
    }
    return draft;
  }, [config, runtimeContext]);

  const [draft, setDraft] = useState<ItemRecord>({ ...emptyDraft });
  const [crossFieldError, setCrossFieldError] = useState<string | null>(null);

  const prefillDraft = useRowPrefill(field.id, config?.subFields, runtimeContext, setDraft);

  const updateDraft = useCallback((fieldName: string, val: string) => {
    setDraft((prev) => {
      const next = { ...prev, [fieldName]: val };
      // Clear any dependentSelect whose parent field just changed
      for (const sf of config?.subFields ?? []) {
        if (sf.inputType === 'dependentSelect' && sf.dependsOn === fieldName) {
          next[sf.field] = '';
        }
      }
      return next;
    });
    setCrossFieldError(null);
    prefillDraft(fieldName, val);
  }, [config?.subFields, prefillDraft]);

  const canAdd = (config?.requiredForAdd ?? []).every((f) => draft[f]?.trim());

  const addItem = useCallback(() => {
    if (!canAdd) return;

    // Declarative cross-field rules
    for (const rule of config?.crossFieldRules ?? []) {
      if (rule.op === 'lte') {
        const lhsVal = Number(draft[rule.lhs]);
        const rhsVal = Number(draft[rule.rhs]);
        if (draft[rule.lhs] && draft[rule.rhs] && !isNaN(lhsVal) && !isNaN(rhsVal) && lhsVal > rhsVal) {
          setCrossFieldError(rule.message);
          return;
        }
      }
    }

    setCrossFieldError(null);
    onChange([...items, { ...draft }]);
    setDraft({ ...emptyDraft });
    setShowAddForm(false);
  }, [canAdd, items, draft, onChange, emptyDraft, config?.crossFieldRules]);

  const handleCancelAdd = useCallback(() => {
    setDraft({ ...emptyDraft });
    setCrossFieldError(null);
    setShowAddForm(false);
  }, [emptyDraft]);

  const removeItem = useCallback(
    (index: number) => {
      onChange(items.filter((_, i) => i !== index));
      if (editingIndex === index) setEditingIndex(null);
    },
    [items, onChange, editingIndex],
  );

  const handleEditStart = useCallback(
    (index: number) => {
      if (editingIndex !== null && editingIndex !== index) {
        if (!window.confirm('You have unsaved changes on another item. Discard them and edit this one instead?')) {
          return;
        }
      }
      setEditingIndex(index);
    },
    [editingIndex],
  );

  const handleSave = useCallback(
    (index: number, updated: ItemRecord) => {
      onChange(items.map((it, i) => (i === index ? updated : it)));
      setEditingIndex(null);
    },
    [items, onChange],
  );

  const handleCancel = useCallback(() => {
    setEditingIndex(null);
  }, []);

  if (!config) return null;

  const atMax = config.maxItems != null && items.length >= config.maxItems;

  return (
    <div>
      {/* Existing items */}
      {items.length > 0 && (
        <div className="space-y-2 mb-5">
          {items.map((item, i) =>
            editingIndex === i ? (
              // EditCard is mounted fresh on each edit-start (key changes).
              // This ensures useState in EditCard initializes cleanly from item — no effect needed.
              <EditCard
                key={`edit-${i}`}
                item={item}
                config={config}
                runtimeContext={runtimeContext}
                listFieldId={field.id}
                idPrefix={`${uid}-edit${i}`}
                heading={config.itemLabel ? `${config.itemLabel} ${i + 1}` : undefined}
                onSave={(updated) => handleSave(i, updated)}
                onCancel={handleCancel}
              />
            ) : (
              <ReadCard
                key={`read-${i}`}
                item={item}
                config={config}
                runtimeContext={runtimeContext}
                heading={config.itemLabel ? `${config.itemLabel} ${i + 1}` : undefined}
                idPrefix={`${uid}-row${i}`}
                onRemove={() => removeItem(i)}
                onEditStart={() => handleEditStart(i)}
              />
            )
          )}
        </div>
      )}

      {/* Add form (opened by dashed button) or dashed "+ Add" trigger */}
      {showAddForm ? (
        <div className="rounded-lg p-4 space-y-4 bg-surface-muted/50">
          <div className={`grid gap-3`} style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}>
            {config.subFields.map((sf) => (
              <div key={sf.field} style={{ gridColumn: sf.colSpan ? `span ${sf.colSpan}` : undefined }}>
                <SubFieldLabel subField={sf} controlId={`${uid}-new-${sf.field}`} className="flex items-center gap-1 mb-1.5" />
                <SubFieldInput
                  controlId={`${uid}-new-${sf.field}`}
                  key={sf.dependsOn ? (draft[sf.dependsOn] ?? '') : sf.field}
                  subField={sf}
                  value={draft[sf.field] ?? ''}
                  onChange={(v) => updateDraft(sf.field, v)}
                  runtimeContext={runtimeContext}
                  parentValue={sf.dependsOn ? (draft[sf.dependsOn] ?? '') : undefined}
                />
                {sf.helpText && (
                  <Text variant="body-sm" color="muted" className="mt-1">{sf.helpText}</Text>
                )}
              </div>
            ))}
          </div>
          {crossFieldError && (
            <Text variant="body-sm" color="danger" className="mt-1">{crossFieldError}</Text>
          )}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={addItem}
              disabled={!canAdd}
              className="btn-primary flex items-center gap-1.5 px-4 py-2 text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Plus size={14} /> {config.addButtonLabel ?? 'Add Item'}
            </button>
            <button
              type="button"
              onClick={handleCancelAdd}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-text-muted border border-border rounded-lg hover:bg-surface-muted transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : atMax ? null : (
        <button
          type="button"
          onClick={() => setShowAddForm(true)}
          className="flex items-center gap-2 w-full rounded-lg border border-dashed border-border bg-surface-muted hover:bg-surface-elevated px-4 py-3 text-sm text-text-muted hover:text-text transition-colors"
        >
          <Plus size={16} />
          {config.addButtonLabel ?? 'Add Item'}
        </button>
      )}
    </div>
  );
}
